import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  applyEdgeChanges,
  applyNodeChanges,
  Controls,
  Background,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import swal from "sweetalert2";
import { parseGraphJSON } from "../utils/workflowGraphJson";
import { normalizeGraphHandles } from "../utils/graphHandles";
import { DEFAULT_LLM_MODEL } from "../utils/modelRegistry";
import { nodeTypes } from "../nodes/nodeTypes";
import { useDynamicNodeTypes } from "../hooks/useDynamicNodeTypes";
import { COMPACT_NODE_SIZE, isCompactNode } from "../utils/dynamicNodeData";
import NodePalette from "./Workflows/NodePalette";


const Workflow = ({ workflow, onWorkflowChange = () => { }, onClose = () => { }, onRunWorkflow = () => { }, dynamicNodes = false, edgeTypes, defaultEdgeOptions, skipHandleNormalization = false }) => {
  const reactFlowRef = useRef(null);
  const [showJSON, setShowJSON] = useState(false);
  const [copyText, setCopyText] = useState("Copy JSON");
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState(null);
  const importFileRef = useRef(null);
  const workflowRef = useRef(workflow);

  console.log("!!! WORKFLOW: ", workflow);

  const [nodes, setNodes] = useState();
  const [edges, setEdges] = useState();

  // When `dynamicNodes` is set, every node renders with the server-driven DynamicNode instead
  // of the prebuilt components; the graph data itself is unchanged either way.
  const dynamicNodeTypes = useDynamicNodeTypes(dynamicNodes);
  const activeNodeTypes = dynamicNodes ? dynamicNodeTypes : nodeTypes;

  useEffect(() => {
    if (typeof workflowRef.current === "string") {
      workflowRef.current = JSON.parse(workflowRef.current);
    }

    if (workflowRef.current?.nodes && !nodes && !edges) {
      workflowRef.current.nodes.forEach((node) => {
        node.dragHandle = ".node-drag-handle";
        node.resizable = true;
        node.resizeHandles = ["se"];

        if (!node.data) {
          node.data = {};
        }
        if (!node.data.onDelete) {
          node.data.onDelete = deleteNodeById;
        }

        if (!node.data.onUpdateData) {
          node.data.onUpdateData = updateDataById;
        }

        if (!node.data.onRenameInput) {
          node.data.onRenameInput = renameInput;
        }
      });

      setNodes([...workflowRef.current.nodes]);

      // Graphs from the generator (or hand-written JSON) can name handles the nodes don't
      // declare, which makes React Flow drop the edge without complaint. Map them first.
      // The dynamic renderer normalises handles from the catalog itself; the prebuilt one uses
      // the static per-type map.
      const normalized = skipHandleNormalization
        ? { nodes: workflowRef.current.nodes, edges: workflowRef.current.edges }
        : normalizeGraphHandles({
            nodes: workflowRef.current.nodes,
            edges: workflowRef.current.edges,
          });
      setEdges([...normalized.edges]);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow]);

  useEffect(() => {
    console.log("!!! NODES OR EDGES CHANGED, CALLING onWorkflowChange: ", nodes, edges);
    onWorkflowChange({ nodes, edges });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);


  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onNodesDelete = useCallback((nodes) => {
    console.log("!!! DELETE NODES: ", nodes);
    setNodes((nds) => nds.filter((n) => !nodes.includes(n.id)));
    setEdges((eds) => eds.filter((e) => !nodes.includes(e.source) && !nodes.includes(e.target)));
  }, []);

  const onEdgesChange = useCallback(
    (changes) => {
      console.log("!!! EDGE CHANGES: ", changes);
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    []
  );

  const onConnect = useCallback(
    (params) => {
      console.log("!!! CONNECT PARAMS: ", params);
      console.log("EDGES BEFORE: ", edges);
      console.log("NODES BEFORE: ", nodes);

      let label = params.sourceHandle;
      setNodes((currentNodes) => {
        const targetNode = currentNodes.find((n) => n.id === params.target);
        const matchingInput = targetNode?.data?.inputs?.find(
          (inp) => inp.id === params.targetHandle
        );
        if (matchingInput) {
          label = matchingInput.label;
        }
        return currentNodes; // no change
      });

      const edgeID = `edge-${params.source}-${params.sourceHandle}-to-${params.target}-${params.targetHandle || "default"}`;
      const newEdge = {
        id: edgeID,
        source: params.source,
        sourceHandle: params.sourceHandle,
        target: params.target,
        targetHandle: params.targetHandle || null,
        label: label,
        type: params.type,
      };
      console.log("!!! NEW EDGE: ", newEdge);

      setEdges((eds) => [...eds, newEdge]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const onEdgeContextMenu = useCallback((event, edge) => {
    event.preventDefault();
    console.log("!!! RIGHT CLICKED EDGE: ", edge);
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  }, []);

  const renameInput = useCallback((nodeId, handleId, newLabel) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.target === nodeId && e.targetHandle === handleId
          ? { ...e, label: newLabel }
          : e
      )
    );
  }, []);

  const deleteNodeById = useCallback((id) => {
    console.log("!!! DELETE NODE ID: ", id);
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  }, []);

  const updateDataById = useCallback((id, newData) => {
    console.log("!!! UPDATE DATA BY ID: ", id, newData);
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            data: {
              ...n.data,
              ...newData,
            },
          };
        }
        return n;
      })
    );
  }, []);

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopyText("Copied!");
        setTimeout(() => setCopyText("Copy JSON"), 3000);
      }, (err) => {
        console.error("Could not copy text: ", err);
        alert("Failed to copy JSON to clipboard.");
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopyText("Copied!");
          setTimeout(() => setCopyText("Copy JSON"), 3000);
        } else {
          alert("Failed to copy JSON to clipboard.");
        }
      }
      catch (err) {
        console.error("Could not copy text: ", err);
        alert("Failed to copy JSON to clipboard.");
      }
      document.body.removeChild(textArea);
    }
  }


  const openImport = () => {
    setShowJSON(false);
    setImportError(null);
    setShowImport(true);
  };

  const closeImport = () => {
    setShowImport(false);
    setImportError(null);
  };

  const applyImportedGraph = (text) => {
    let result;
    try {
      result = parseGraphJSON(text, {
        validNodeTypes: Object.keys(activeNodeTypes),
        callbacks: {
          onDelete: deleteNodeById,
          onUpdateData: updateDataById,
          onRenameInput: renameInput,
        },
      });
    } catch (error) {
      setImportError(error.message);
      return false;
    }

    // Setting these two is all that is required: the effect above propagates them to
    // onWorkflowChange, which is what the workflow save path persists.
    const normalized = normalizeGraphHandles({ nodes: result.nodes, edges: result.edges });
    setNodes(normalized.nodes);
    setEdges(normalized.edges);

    setImportError(null);
    setImportText("");
    setShowImport(false);

    // The import replaces every node, so recentre the viewport on the new graph.
    window.setTimeout(() => {
      reactFlowRef.current?.fitView({ padding: 0.2, maxZoom: 1 });
    }, 100);

    const summary = `Imported ${result.nodes.length} node(s) and ${result.edges.length} edge(s).`;
    const message = result.warnings.length > 0
      ? `${summary}\n\n${result.warnings.map((warning) => `• ${warning}`).join("\n")}`
      : summary;

    swal.fire({
      title: result.warnings.length > 0 ? "Graph imported with warnings" : "Graph imported",
      text: message,
      icon: result.warnings.length > 0 ? "warning" : "success",
      confirmButtonText: "OK",
    });

    return true;
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // let the same file be picked again
    if (!file) {
      return;
    }
    try {
      setImportText(await file.text());
      setImportError(null);
    } catch (error) {
      setImportError(`Could not read "${file.name}": ${error.message}`);
    }
  };

  // Add a node from its server definition (type, default_data, layout size).
  const addNodeFromDefinition = (definition) => {
    const type = definition?.type;
    if (!type) return;

    // Entry/output nodes are small squares; everything else gets the definition's width (or a
    // comfortable fallback) so the fields aren't cramped.
    const compact = isCompactNode(definition);
    const width = compact
      ? COMPACT_NODE_SIZE
      : typeof definition.layout?.width === "number" && definition.layout.width > 0
        ? definition.layout.width
        : 360;

    // Dynamic nodes let React Flow measure their height from the rendered content, so every
    // parameter is visible without scrolling. The prebuilt components keep their declared size.
    const size = compact
      ? { width, height: COMPACT_NODE_SIZE }
      : dynamicNodes
        ? { width }
        : {
            width,
            height: typeof definition.layout?.height === "number" ? definition.layout.height : "auto",
          };

    const data = {
      ...(definition.defaultData ?? {}),
      onDelete: deleteNodeById,
      onUpdateData: updateDataById,
      onRenameInput: renameInput,
      label: definition.name ?? `${type} Node`,
    };

    // LLM nodes default to the built-in model unless the definition already supplies one.
    if (type === "llmNode" && !data.model) {
      data.model = DEFAULT_LLM_MODEL;
    }

    const newNode = {
      id: `node-${uuidv4()}`,
      position: { x: 250, y: 25 + (nodes?.length ?? 0) * 75 },
      data,
      ...size,
      type,
      deletable: true,
    };

    setNodes((nds) => (nds ?? []).concat(newNode));
  };


  /*if (nodes.length === 0) {
    return <div>Loading...</div>;
  }*/

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: "10px", height: "100%", width: "100%" }}>
      <div style={{ width: "240px", height: "calc(100vh - 175px)", overflow: "auto", padding: "10px 6px 10px 10px", boxSizing: "border-box", flexShrink: 0 }}>
        <NodePalette onAdd={addNodeFromDefinition} />
      </div>
      <div style={{ flexGrow: 1, border: "1px solid var(--main-border-color)", height: "100%" }}>
        <ReactFlow
          ref={reactFlowRef}
          nodeTypes={activeNodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onNodesDelete={onNodesDelete}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={(edges) => {
            console.log("!!! DELETE EDGES: ", edges);
            setEdges((eds) => eds.filter((e) => !edges.includes(e.id)));
          }}
          onEdgeContextMenu={onEdgeContextMenu}
          onConnect={onConnect}
          minZoom={0.05}
          maxZoom={2}
          fitView
          fitViewOptions={{
            minZoom: 0.5,
            maxZoom: 1,
          }}
        >
          <Background />
          <Controls />
          <div style={{ position: "absolute", top: 10, right: 10, zIndex: 4 }}>
            <button onClick={() => {
              onRunWorkflow();
            }} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>Run Workflow</button>
            <button onClick={() => {
              setShowImport(false);
              setShowJSON(true);
            }} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>View JSON</button>
            <button onClick={openImport} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>Import JSON</button>
          </div>

        </ReactFlow>
      </div>
      <div style={{
        display: showJSON ? "block" : "none",
        position: "absolute",
        top: "10%",
        left: "10%",
        width: "80%",
        height: "80%",
        backgroundColor: "var(--main-bg)",
        border: "1px solid var(--main-border-color)",
        padding: "20px",
        overflow: "hidden",
        zIndex: 10,
      }}
      >
        <div>
          <button onClick={() => {
            copyToClipboard(JSON.stringify({ nodes, edges }, null, 2));
          }} style={{ position: "absolute", top: 10, left: 10, padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>{copyText}</button>
          <button onClick={() => setShowJSON(false)} style={{ position: "absolute", top: 10, right: 10, padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>Close</button>
        </div>
        <div style={{ width: "100%", height: "calc(100% - 40px)", overflow: "auto", marginTop: "20px" }}>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{JSON.stringify({ nodes, edges }, null, 2)}</pre>
        </div>
      </div>

      <div style={{
        display: showImport ? "flex" : "none",
        flexDirection: "column",
        gap: "10px",
        position: "absolute",
        top: "10%",
        left: "10%",
        width: "80%",
        height: "80%",
        backgroundColor: "var(--main-bg)",
        border: "1px solid var(--main-border-color)",
        padding: "20px",
        overflow: "hidden",
        zIndex: 10,
        color: "var(--main-font-color)",
      }}
      >
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
          <strong style={{ fontSize: "16px" }}>Import Graph JSON</strong>
          <span style={{ fontSize: "13px", opacity: 0.7 }}>
            Paste a {"{ nodes, edges }"} object. It replaces the current graph; nothing is saved until you click Save Changes.
          </span>
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "row", gap: "10px" }}>
            <button onClick={() => importFileRef.current?.click()} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>Load File…</button>
            <button onClick={() => applyImportedGraph(importText)} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer", backgroundColor: "var(--button-bg)", color: "var(--button-font-color)", border: "none", borderRadius: "4px" }}>Import</button>
            <button onClick={closeImport} style={{ padding: "5px 10px", fontSize: "14px", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
        <input
          ref={importFileRef}
          type="file"
          accept=".json,application/json"
          onChange={handleImportFile}
          style={{ display: "none" }}
        />
        {importError &&
          <div style={{ padding: "8px 10px", fontSize: "13px", border: "1px solid #c0392b", borderRadius: "4px", backgroundColor: "rgba(192, 57, 43, 0.1)", color: "#c0392b" }}>
            {importError}
          </div>
        }
        <textarea
          value={importText}
          onChange={(event) => {
            setImportText(event.target.value);
            if (importError) {
              setImportError(null);
            }
          }}
          spellCheck={false}
          placeholder={`{
  "nodes": [
    {
      "id": "input",
      "type": "inputNode",
      "position": { "x": 100, "y": 200 },
      "data": {}
    }
  ],
  "edges": []
}`}
          style={{
            flexGrow: 1,
            width: "100%",
            resize: "none",
            padding: "10px",
            fontFamily: "monospace",
            fontSize: "13px",
            lineHeight: "1.5",
            border: "1px solid var(--main-border-color)",
            borderRadius: "4px",
            backgroundColor: "var(--content-item-bg)",
            color: "var(--main-font-color)",
          }}
        />
      </div>


    </div >
  );
};

export default Workflow;

